import {
  KomboIntegrationListFieldsFragment,
  KomboSyncRunStatus,
} from 'generated/graphql';
import { formatTimestamp } from 'helpers/date';
import { GridCellParams } from '@spotted-zebra-uk/ui';
import { Tag, TagColor, TagSize } from '@spotted-zebra-uk/ui-components';
import styles from './Kombo.module.scss';

/**
 * Derived from the presence of the provider-side integration ids rather than
 * a status field — an id exists exactly when that half is connected in Kombo.
 */
export function connectedLabel(
  integration: Pick<
    KomboIntegrationListFieldsFragment,
    'atsIntegrationId' | 'assessmentIntegrationId'
  >
): string {
  const ats = Boolean(integration.atsIntegrationId);
  const assessment = Boolean(integration.assessmentIntegrationId);

  if (ats && assessment) return 'ATS + Assessment';
  if (ats) return 'ATS';
  if (assessment) return 'Assessment only';
  return '—';
}

export function EnabledTag({ enabled }: { enabled: boolean }) {
  return (
    <Tag
      isDismissible={false}
      isPresentational
      tagColor={enabled ? TagColor.GREEN : TagColor.DISABLED}
      size={TagSize.LARGE}
    >
      {enabled ? 'Enabled' : 'Disabled'}
    </Tag>
  );
}

const syncRunStatusInfo: Record<
  KomboSyncRunStatus,
  { label: string; color: TagColor }
> = {
  [KomboSyncRunStatus.Completed]: { label: 'Completed', color: TagColor.GREEN },
  [KomboSyncRunStatus.Running]: {
    label: 'Running',
    color: TagColor.YELLOW_BACK,
  },
  [KomboSyncRunStatus.Pending]: {
    label: 'Pending',
    color: TagColor.YELLOW_BACK,
  },
  [KomboSyncRunStatus.Paused]: { label: 'Paused', color: TagColor.DISABLED },
  [KomboSyncRunStatus.Cancelled]: {
    label: 'Cancelled',
    color: TagColor.DISABLED,
  },
  [KomboSyncRunStatus.Failed]: { label: 'Failed', color: TagColor.RED },
};

type LatestSyncRun = NonNullable<
  KomboIntegrationListFieldsFragment['latestSyncRun']
>;

/**
 * Status tag plus, once the run has finished, its completion date. A run
 * still in flight has no `completedAt`, so it shows the tag alone rather than
 * an em dash next to it.
 */
export function LastSyncCell({ run }: { run: LatestSyncRun | null }) {
  if (!run) return <>—</>;

  const { label, color } = syncRunStatusInfo[run.status];

  return (
    <span className={styles.lastSync}>
      <Tag isDismissible={false} isPresentational tagColor={color} size={TagSize.LARGE}>
        {label}
      </Tag>
      {run.completedAt && <span>{formatTimestamp(run.completedAt)}</span>}
    </span>
  );
}

export type KomboIntegrationRow = {
  /** DataGrid row id — the integration's uuid. */
  id: string;
  companyId: number;
  company: string;
  tool: string;
  connected: string;
  enabled: boolean;
  latestSyncRun: LatestSyncRun | null;
};

export function parseIntegrationRows(
  integrations: KomboIntegrationListFieldsFragment[] | undefined,
  /**
   * SZ company names keyed by company id, from the companion company lookup.
   * A miss (lookup not loaded, or company not returned) falls back to
   * `Company #<id>`.
   */
  companyNames?: Map<number, string>
): KomboIntegrationRow[] {
  if (!integrations) return [];

  return integrations.map(integration => {
    const companyName = companyNames?.get(integration.companyId);

    return {
      id: integration.id,
      companyId: integration.companyId,
      company: companyName
        ? `${companyName} (#${integration.companyId})`
        : `Company #${integration.companyId}`,
      tool: integration.tool,
      connected: connectedLabel(integration),
      enabled: integration.enabled,
      latestSyncRun: integration.latestSyncRun ?? null,
    };
  });
}

export const INTEGRATION_COLUMNS = [
  { field: 'company', headerName: 'Company', flex: 0.4, minWidth: 220 },
  { field: 'tool', headerName: 'Tool', flex: 0.2, minWidth: 140 },
  { field: 'connected', headerName: 'Connected', flex: 0.2, minWidth: 160 },
  {
    field: 'enabled',
    headerName: 'Enabled',
    minWidth: 130,
    renderCell: (params: GridCellParams) => (
      <EnabledTag enabled={Boolean(params.value)} />
    ),
  },
  {
    field: 'latestSyncRun',
    headerName: 'Last sync',
    flex: 0.2,
    minWidth: 200,
    sortable: false,
    renderCell: (params: GridCellParams) => (
      <LastSyncCell run={params.row.latestSyncRun} />
    ),
  },
];
